import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingBag, MapPin, User, Menu, LogOut, ShieldCheck, Bike, Globe, Wallet, KeyRound, Search, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import NotificationBell from "@/components/NotificationBell";
import AddressSelector from "@/components/AddressSelector";
import { roleHome } from "@/utils/roles";
import { isNativeApp } from "@/utils/platform";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const LANGS = [
  { code: "fr", flag: "🇫🇷", label: "Français" },
  { code: "en", flag: "🇬🇧", label: "English" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Nouveau header accueil (sélecteur d'adresse, prénom, sans loupe, logo empilé)
  // UNIQUEMENT dans l'app mobile Capacitor — le site web garde son header d'origine.
  const isHome = pathname === "/" && isNativeApp;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const setLang = (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem("neldaari_lang", code); } catch (e) { void e; }
  };

  const currentLang = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  // Clients et visiteurs disposent de la barre de navigation basse (mobile) :
  // Accueil · Restaurants · Panier · Commandes · Compte. Sur mobile, le header
  // est donc épuré (logo + recherche + cloche) — aucune icône en double.
  const isClientish = !user || user.role === "client";

  // Note app mobile (WKWebView) : les menus utilisent Radix DropdownMenu,
  // rendus en portail dans <body> — les dropdowns "maison" positionnés en
  // absolu dans le header sticky + backdrop-blur ne recevaient pas les taps.
  const itemCls = "flex items-center gap-2 px-3 py-2 rounded-xl text-sm cursor-pointer";

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-sand" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-4">
        {/* Logo → work screen for partners, public home for clients.
            Identique sur tous les onglets : icône + « NelDaari », même taille. */}
        <Link to={roleHome(user?.role)} className="flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0" data-testid="nav-logo">
          <img src="/logo-neldaari.png" alt="NelDaari" className="w-8 h-8 sm:w-9 sm:h-9 shrink-0" />
          <span className="font-display text-xl sm:text-2xl font-semibold text-ink whitespace-nowrap">NelDaari</span>
        </Link>

        {/* Sélecteur d'adresse — page d'accueil client uniquement (style Uber Eats), mobile */}
        {isHome && isClientish && (
          <div className="md:hidden flex-1 min-w-0 flex justify-center">
            <AddressSelector variant="mobile" />
          </div>
        )}

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
          <NavLink to="/restaurants" data-testid="nav-restaurants" className={({isActive}) => isActive ? "text-brand-red" : "text-ink hover:text-brand-red"}>{t("nav.restaurants")}</NavLink>
          <NavLink to="/about" data-testid="nav-about" className={({isActive}) => isActive ? "text-brand-red" : "text-ink hover:text-brand-red"}>{t("nav.about")}</NavLink>
          <NavLink to="/contact" data-testid="nav-contact" className={({isActive}) => isActive ? "text-brand-red" : "text-ink hover:text-brand-red"}>{t("nav.contact")}</NavLink>
          <div className="hidden lg:flex items-center gap-2 text-mute">
            {isHome && isClientish ? (
              <AddressSelector variant="desktop" />
            ) : (
              <><MapPin className="w-4 h-4" /> {t("nav.city")}</>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          {/* Recherche — mobile uniquement (clients/visiteurs), masquée sur l'accueil
              qui possède déjà sa propre barre de recherche */}
          {isClientish && !isHome && (
            <Link to="/restaurants" data-testid="nav-search-mobile" aria-label="Recherche" className="md:hidden rounded-full bg-elevated hover:bg-[#EBE8E0] p-2 shrink-0">
              <Search className="w-4 h-4" />
            </Link>
          )}

          {/* Sélecteur de langue — desktop uniquement (le header mobile client reste épuré) */}
          <div className="hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="lang-switcher" className="rounded-full bg-elevated hover:bg-[#EBE8E0] px-2.5 py-2 text-sm font-medium inline-flex items-center gap-1.5">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{currentLang.flag}</span>
                  <span className="hidden md:inline uppercase text-xs">{currentLang.code}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 rounded-2xl p-1.5" data-testid="lang-menu">
                {LANGS.map((l) => (
                  <DropdownMenuItem key={l.code} data-testid={`lang-opt-${l.code}`} onSelect={() => setLang(l.code)} className={`${itemCls} ${i18n.language === l.code ? "font-semibold bg-elevated" : ""}`}>
                    <span>{l.flag}</span> {l.label}
                    {i18n.language === l.code && <Check className="w-3.5 h-3.5 ml-auto" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <NotificationBell />

          {(!user || user.role === "client") && (
            <Link to="/cart" data-testid="nav-cart" className="relative hidden md:inline-flex items-center gap-2 rounded-full bg-elevated hover:bg-[#EBE8E0] px-2.5 sm:px-4 py-2 text-sm font-medium shrink-0">
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">{t("nav.cart")}</span>
              {count > 0 && (
                <span data-testid="cart-badge" className="absolute -top-1 -right-1 bg-brand-red text-white text-xs rounded-full w-5 h-5 grid place-items-center font-semibold">{count}</span>
              )}
            </Link>
          )}

          {user ? (
            <div className={isClientish ? "hidden md:block" : ""}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="rounded-full bg-elevated hover:bg-[#EBE8E0] p-2">
                    <User className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-2xl p-2" data-testid="user-menu-content">
                  <div className="px-3 py-2 border-b border-sand mb-1">
                    <div className="text-sm font-semibold text-ink truncate">{user.name}</div>
                    <div className="text-xs text-mute truncate">{user.phone || user.email}</div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-red mt-1">{user.role}</div>
                  </div>
                  <DropdownMenuItem asChild data-testid="user-menu-profile" className={itemCls}>
                    <Link to="/profile"><User className="w-4 h-4" /> {t("nav.profile")}</Link>
                  </DropdownMenuItem>
                  {user.role === "restaurant" && (
                    <DropdownMenuItem asChild data-testid="user-menu-partner-profile" className={itemCls}>
                      <Link to="/partner-profile"><Wallet className="w-4 h-4" /> {t("nav.partnerProfile")}</Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "restaurant_group" && (
                    <DropdownMenuItem asChild data-testid="user-menu-group-dashboard" className={itemCls}>
                      <Link to="/dashboard/groupe"><Wallet className="w-4 h-4" /> Tableau de bord groupe</Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "driver" && (
                    <>
                      <DropdownMenuItem asChild data-testid="user-menu-driver-dashboard" className={itemCls}>
                        <Link to="/dashboard/livreur"><Bike className="w-4 h-4" /> Mon tableau de bord</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild data-testid="user-menu-partner-profile" className={itemCls}>
                        <Link to="/partner-profile"><Wallet className="w-4 h-4" /> {t("nav.partnerProfile")}</Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild data-testid="user-menu-admin-dashboard" className={itemCls}>
                      <Link to="/dashboard/admin"><ShieldCheck className="w-4 h-4" /> Tableau de bord</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild data-testid="user-menu-change-password" className={itemCls}>
                    <Link to="/change-password"><KeyRound className="w-4 h-4" /> {t("cp.title")}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="user-menu-logout" onSelect={handleLogout} className={`${itemCls} text-brand-red focus:text-brand-red`}>
                    <LogOut className="w-4 h-4" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden md:inline-block rounded-full bg-brand-red text-white hover:bg-[#C81F2D] px-3 sm:px-5 py-2 text-xs sm:text-sm font-semibold shadow-sm whitespace-nowrap shrink-0">{t("nav.login")}</Link>
          )}

          {/* Menu ☰ — mobile, tous les rôles : liens de navigation (non-clients)
              + sélecteur de langue de l'application (tout le monde).
              Les clients gardent la barre basse pour la navigation principale. */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-burger" aria-label="Menu" className="rounded-full bg-elevated p-2 shrink-0">
                  <Menu className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl p-2" data-testid="burger-menu">
                {!isClientish && (
                  <>
                    <DropdownMenuItem asChild className={itemCls}>
                      <Link to="/restaurants">{t("nav.restaurants")}</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className={itemCls}>
                      <Link to="/about">{t("nav.about")}</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className={itemCls}>
                      <Link to="/contact">{t("nav.contact")}</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-mute flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> {t("nav.language")}</div>
                {LANGS.map((l) => (
                  <DropdownMenuItem key={l.code} data-testid={`mobile-lang-${l.code}`} onSelect={() => setLang(l.code)} className={`${itemCls} ${i18n.language === l.code ? "font-semibold bg-elevated" : ""}`}>
                    <span>{l.flag}</span> {l.label}
                    {i18n.language === l.code && <Check className="w-3.5 h-3.5 ml-auto" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
